/**
 * Performance Benchmarks for Versioning Operations
 *
 * This module provides comprehensive performance monitoring and benchmarking
 * for all versioning operations to ensure optimal user experience.
 */

import { useEffect, useState } from "react";
import type { ProjectSnapshot } from "@/types";

export interface PerformanceBenchmark {
  operation: string;
  startTime: number;
  endTime: number;
  duration: number;
  dataSize: number;
  memoryUsage: number;
  success: boolean;
  errorMessage?: string;
  metadata: {
    componentCount?: number;
    pageCount?: number;
    changeCount?: number;
    conflictCount?: number;
    snapshotSize?: number;
  };
}

export interface PerformanceMetrics {
  averageDuration: number;
  minDuration: number;
  maxDuration: number;
  operationCount: number;
  successRate: number;
  throughput: number; // operations per second
  memoryEfficiency: number;
  benchmarks: PerformanceBenchmark[];
}

export interface PerformanceTargets {
  // Target times in milliseconds
  createVersion: 100;
  compareVersions: 50;
  restoreVersion: 200;
  createSnapshot: 80;
  detectConflicts: 150;
  trackChange: 5;
  stateUpdate: 16; // 60fps target
}

interface PendingBenchmark {
  operation: string;
  startTime: number;
  memoryUsage: number;
  metadata: PerformanceBenchmark["metadata"];
}

/**
 * Performance monitoring and benchmarking system
 */
export class VersioningPerformanceMonitor {
  private static instance: VersioningPerformanceMonitor;
  private benchmarks: Map<string, PerformanceBenchmark[]> = new Map();
  private pendingBenchmarks: Map<string, PendingBenchmark> = new Map();
  private isMonitoringEnabled = true;
  private targets: PerformanceTargets = {
    createVersion: 100,
    compareVersions: 50,
    restoreVersion: 200,
    createSnapshot: 80,
    detectConflicts: 150,
    trackChange: 5,
    stateUpdate: 16,
  };

  static getInstance(): VersioningPerformanceMonitor {
    if (!VersioningPerformanceMonitor.instance) {
      VersioningPerformanceMonitor.instance =
        new VersioningPerformanceMonitor();
    }
    return VersioningPerformanceMonitor.instance;
  }

  /**
   * Enable or disable performance monitoring
   */
  setMonitoringEnabled(enabled: boolean): void {
    this.isMonitoringEnabled = enabled;
  }

  /**
   * Update performance targets
   */
  updateTargets(newTargets: Partial<PerformanceTargets>): void {
    this.targets = { ...this.targets, ...newTargets };
  }

  /**
   * Start benchmarking an operation
   */
  startBenchmark(
    operation: string,
    metadata: PerformanceBenchmark["metadata"] = {},
  ): string {
    if (!this.isMonitoringEnabled) return "";

    const benchmarkId = `${operation}_${Date.now()}_${Math.random()}`;
    const startTime = performance.now();
    const memoryUsage = this.getMemoryUsage();

    // Store partial benchmark for completion later
    this.pendingBenchmarks.set(benchmarkId, {
      operation,
      startTime,
      memoryUsage,
      metadata,
    });

    return benchmarkId;
  }

  /**
   * Complete benchmarking an operation
   */
  completeBenchmark(
    benchmarkId: string,
    success: boolean = true,
    errorMessage?: string,
    additionalMetadata: Partial<PerformanceBenchmark["metadata"]> = {},
  ): PerformanceBenchmark | null {
    if (!this.isMonitoringEnabled || !benchmarkId) return null;

    const pending = this.pendingBenchmarks.get(benchmarkId);
    if (!pending) return null;

    const endTime = performance.now();
    const duration = endTime - pending.startTime;
    const currentMemoryUsage = this.getMemoryUsage();
    const dataSize = this.estimateDataSize(additionalMetadata);

    const benchmark: PerformanceBenchmark = {
      operation: pending.operation,
      startTime: pending.startTime,
      endTime,
      duration,
      dataSize,
      memoryUsage: currentMemoryUsage - pending.memoryUsage,
      success,
      errorMessage,
      metadata: { ...pending.metadata, ...additionalMetadata },
    };

    // Store benchmark
    const operationBenchmarks = this.benchmarks.get(pending.operation) || [];
    operationBenchmarks.push(benchmark);

    // Keep only last 100 benchmarks per operation
    if (operationBenchmarks.length > 100) {
      operationBenchmarks.shift();
    }

    this.benchmarks.set(pending.operation, operationBenchmarks);

    // Clean up pending
    this.pendingBenchmarks.delete(benchmarkId);

    // Emit warning if performance target exceeded
    this.checkPerformanceTarget(benchmark);

    return benchmark;
  }

  /**
   * Benchmark an async operation
   */
  async benchmarkOperation<T>(
    operation: string,
    operationFn: () => Promise<T>,
    metadata: PerformanceBenchmark["metadata"] = {},
  ): Promise<T> {
    const benchmarkId = this.startBenchmark(operation, metadata);

    try {
      const result = await operationFn();

      // Extract metadata from result based on its structure
      const resultMetadata = this.extractResultMetadata(result);

      this.completeBenchmark(
        benchmarkId,
        true,
        undefined,
        resultMetadata
      );

      return result;
    } catch (error) {
      this.completeBenchmark(
        benchmarkId,
        false,
        error instanceof Error ? error.message : "Unknown error"
      );
      throw error;
    }
  }

  /**
   * Extract metadata from operation result
   */
  private extractResultMetadata(result: unknown): Partial<PerformanceBenchmark["metadata"]> {
    const metadata: Partial<PerformanceBenchmark["metadata"]> = {};

    // Check if result has snapshot property
    if (result && typeof result === 'object' && 'snapshot' in result) {
      const snapshot = (result as { snapshot: ProjectSnapshot }).snapshot;
      metadata.snapshotSize = JSON.stringify(snapshot).length;
    }

    // Check if result has conflicts property
    if (result && typeof result === 'object' && 'conflicts' in result) {
      const conflicts = (result as { conflicts: unknown[] }).conflicts || [];
      metadata.conflictCount = conflicts.length;
    }

    // Check if result has changes property
    if (result && typeof result === 'object' && 'changes' in result) {
      const changes = (result as { changes: unknown[] }).changes || [];
      metadata.changeCount = changes.length;
    }

    return metadata;
  }

  /**
   * Get memory usage (cross-browser compatible)
   */
  private getMemoryUsage(): number {
    try {
      // Check for performance.memory API
      const perfMemory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      if (perfMemory && 'usedJSHeapSize' in perfMemory) {
        return perfMemory.usedJSHeapSize;
      }
    } catch {
      // Silently handle unsupported browsers
    }

    // Fallback for browsers without memory API
    return 0;
  }

  /**
   * Estimate data size from metadata
   */
  private estimateDataSize(metadata: Partial<PerformanceBenchmark["metadata"]>): number {
    let size = 0;

    if (metadata.componentCount) size += metadata.componentCount * 200; // ~200 bytes per component
    if (metadata.pageCount) size += metadata.pageCount * 100; // ~100 bytes per page
    if (metadata.changeCount) size += metadata.changeCount * 150; // ~150 bytes per change
    if (metadata.snapshotSize) size += metadata.snapshotSize;

    return size;
  }

  /**
   * Check if performance target was met and emit warning if not
   */
  private checkPerformanceTarget(benchmark: PerformanceBenchmark): void {
    const target = this.targets[benchmark.operation as keyof PerformanceTargets];
    if (target && benchmark.duration > target) {
      console.warn(
        `Performance target exceeded for ${benchmark.operation}: ${benchmark.duration}ms > ${target}ms`
      );
    }
  }

  /**
   * Get performance metrics for a specific operation
   */
  getMetricsForOperation(operation: string): PerformanceMetrics | null {
    const benchmarks = this.benchmarks.get(operation);
    if (!benchmarks || benchmarks.length === 0) return null;

    const durations = benchmarks.map(b => b.duration);
    const successfulOperations = benchmarks.filter(b => b.success);

    return {
      averageDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
      minDuration: Math.min(...durations),
      maxDuration: Math.max(...durations),
      operationCount: benchmarks.length,
      successRate: successfulOperations.length / benchmarks.length,
      throughput: benchmarks.length / (Math.max(...benchmarks.map(b => b.endTime)) - Math.min(...benchmarks.map(b => b.startTime))) * 1000,
      memoryEfficiency: benchmarks.reduce((sum, b) => sum + b.memoryUsage, 0) / benchmarks.length,
      benchmarks,
    };
  }

  /**
   * Get all performance metrics
   */
  getAllMetrics(): { [operation: string]: PerformanceMetrics } {
    const metrics: { [operation: string]: PerformanceMetrics } = {};

    for (const [operation] of this.benchmarks) {
      const operationMetrics = this.getMetricsForOperation(operation);
      if (operationMetrics) {
        metrics[operation] = operationMetrics;
      }
    }

    return metrics;
  }

  /**
   * Clear all benchmarks
   */
  clearBenchmarks(): void {
    this.benchmarks.clear();
    this.pendingBenchmarks.clear();
  }

  /**
   * Get performance targets
   */
  getTargets(): PerformanceTargets {
    return { ...this.targets };
  }
}

// Global instance
export const performanceMonitor = VersioningPerformanceMonitor.getInstance();

/**
 * Utility function to benchmark versioning operations
 */
export const benchmarkVersioningOperation = async <T>(
  operation: string,
  operationFn: () => Promise<T>,
  metadata: PerformanceBenchmark["metadata"] = {},
): Promise<T> => {
  return performanceMonitor.benchmarkOperation(operation, operationFn, metadata);
};

/**
 * React hook to monitor versioning performance
 */
export const useVersioningPerformance = (): {
  metrics: { [operation: string]: PerformanceMetrics };
  clearMetrics: () => void;
  isMonitoringEnabled: boolean;
  setMonitoringEnabled: (enabled: boolean) => void;
} => {
  const [metrics, setMetrics] = useState<{
    [operation: string]: PerformanceMetrics;
  }>({});

  useEffect(() => {
    // Update metrics every 5 seconds
    const interval = setInterval(() => {
      setMetrics(performanceMonitor.getAllMetrics());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return {
    metrics,
    clearMetrics: () => {
      performanceMonitor.clearBenchmarks();
      setMetrics({});
    },
    isMonitoringEnabled: true,
    setMonitoringEnabled: (enabled: boolean) => {
      performanceMonitor.setMonitoringEnabled(enabled);
    },
  };
};

interface VersioningPerformanceStore {
  createVersion: (data: unknown) => Promise<unknown>;
  compareVersions: (
    fromVersion: number,
    toVersion: number,
  ) => Promise<unknown>;
  restoreVersion: (versionNumber: number) => Promise<unknown>;
  generateSnapshot: (projectId: string) => Promise<unknown>;
  trackComponentChange: (...args: unknown[]) => void;
}

/**
 * Performance middleware for versioning store
 */
export const applyPerformanceMonitoring = (
  store: VersioningPerformanceStore,
) => {
  // We'll wrap methods that exist in the store
  const originalCreateVersion = store.createVersion;
  if (originalCreateVersion) {
    store.createVersion = async (data) => {
      return benchmarkVersioningOperation("createVersion", () =>
        originalCreateVersion(data),
      );
    };
  }

  const originalCompareVersions = store.compareVersions;
  if (originalCompareVersions) {
    store.compareVersions = async (fromVersion, toVersion) => {
      return benchmarkVersioningOperation("compareVersions", () =>
        originalCompareVersions(fromVersion, toVersion),
      );
    };
  }

  const originalRestoreVersion = store.restoreVersion;
  if (originalRestoreVersion) {
    store.restoreVersion = async (versionNumber) => {
      return benchmarkVersioningOperation("restoreVersion", () =>
        originalRestoreVersion(versionNumber),
      );
    };
  }

  const originalGenerateSnapshot = store.generateSnapshot;
  if (originalGenerateSnapshot) {
    store.generateSnapshot = async (projectId) => {
      return benchmarkVersioningOperation("createSnapshot", () =>
        originalGenerateSnapshot(projectId),
      );
    };
  }

  const originalTrackComponentChange = store.trackComponentChange;
  if (originalTrackComponentChange) {
    store.trackComponentChange = (...args) => {
      return benchmarkVersioningOperation("trackChange", () =>
        Promise.resolve(originalTrackComponentChange(...args)),
      );
    };
  }
};
